import { useState, useRef, useEffect } from "react";
import { searchSkills } from "../../data/skills";

export default function SkillsInput({ value = [], onChange, placeholder = "Type a skill..." }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Update suggestions as user types
  useEffect(() => {
    if (input.trim().length > 0) {
      const results = searchSkills(input).filter((s) => !value.includes(s));
      setSuggestions(results);
      setOpen(results.length > 0);
      setActiveIndex(-1);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  }, [input, value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addSkill = (skill) => {
    const exists = value.some((s) => s.toLowerCase() === skill.toLowerCase());
    if (!exists) {
      onChange([...value, skill]);
    }
    setInput("");
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const removeSkill = (skill) => {
    onChange(value.filter((s) => s !== skill));
  };

  // Inline editing of an existing skill chip (click the label to edit)
  const [editing, setEditing] = useState(null); // { index, text }

  const commitEdit = () => {
    if (!editing) return;
    const text = editing.text.trim().replace(/,+$/, "");
    const duplicate = value.some((s, i) => i !== editing.index && s.toLowerCase() === text.toLowerCase());
    if (text.length >= 2 && !duplicate) {
      onChange(value.map((s, i) => (i === editing.index ? text : s)));
    } else if (text.length === 0) {
      onChange(value.filter((_, i) => i !== editing.index));
    }
    setEditing(null);
  };

  const addCustomSkill = () => {
    const skill = input.trim().replace(/,+$/, "");
    if (skill.length < 2) return;
    addSkill(skill);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        addSkill(suggestions[activeIndex]);
      } else {
        addCustomSkill();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && input === "" && value.length > 0) {
      // Remove last skill on backspace when input is empty
      removeSkill(value[value.length - 1]);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Tags + Input container */}
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          minHeight: 44,
          border: "1px solid #E5E7EB",
          borderRadius: 8,
          padding: "6px 10px",
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          background: "white",
          cursor: "text",
          transition: "border-color .15s",
        }}
        onFocus={() => {}}
      >
        {/* Skill tags */}
        {value.map((skill, index) => (
          <span
            key={skill}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 12, fontWeight: 500,
              padding: "3px 8px", borderRadius: 5,
              background: "#EEF2FF", color: "#4338CA",
              border: "1px solid #C7D2FE",
            }}
          >
            {editing?.index === index ? (
              <input
                autoFocus
                value={editing.text}
                onChange={(e) => setEditing({ index, text: e.target.value })}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commitEdit(); }
                  else if (e.key === "Escape") setEditing(null);
                }}
                style={{
                  border: "none", outline: "none", background: "white",
                  fontSize: 12, fontWeight: 500, color: "#4338CA", borderRadius: 3,
                  padding: "0 4px", width: Math.max(skill.length, editing.text.length, 4) + 2 + "ch",
                  fontFamily: "inherit",
                }}
              />
            ) : (
              <span
                title="Click to edit"
                onClick={() => setEditing({ index, text: skill })}
                style={{ cursor: "text" }}
              >
                {skill}
              </span>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeSkill(skill); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#6366F1", fontSize: 14, lineHeight: 1,
                padding: 0, display: "flex", alignItems: "center",
              }}
            >
              ×
            </button>
          </span>
        ))}

        {/* Text input */}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          placeholder={value.length === 0 ? placeholder : ""}
          style={{
            border: "none", outline: "none",
            fontSize: 13, color: "#111827",
            background: "transparent",
            flex: 1, minWidth: 140,
            fontFamily: "inherit",
          }}
        />
      </div>

      {/* Dropdown */}
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: "white", borderRadius: 8,
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 16px rgba(0,0,0,.1)",
            zIndex: 50, overflow: "hidden",
          }}
        >
          {suggestions.map((skill, i) => (
            <div
              key={skill}
              onMouseDown={(e) => { e.preventDefault(); addSkill(skill); }}
              onMouseEnter={() => setActiveIndex(i)}
              style={{
                padding: "10px 14px",
                fontSize: 13,
                color: i === activeIndex ? "#4338CA" : "#374151",
                background: i === activeIndex ? "#EEF2FF" : "white",
                cursor: "pointer",
                display: "flex", alignItems: "center",
                justifyContent: "space-between",
                borderBottom: i < suggestions.length - 1 ? "1px solid #F9FAFB" : "none",
                transition: "background .1s",
              }}
            >
              <span>{skill}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke={i === activeIndex ? "#6366F1" : "#D1D5DB"} strokeWidth="2" width="14" height="14">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          ))}
          <div style={{ padding: "7px 14px", fontSize: 11, color: "#9CA3AF", borderTop: "1px solid #F3F4F6", background: "#FAFAFA" }}>
            ↑↓ navigate · Enter to add · Backspace to remove last
          </div>
        </div>
      )}

      {/* Helper text */}
      {value.length === 0 && (
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
          Start typing to search, or press Enter to add a custom skill
        </div>
      )}
    </div>
  );
}
